<template>
  <ValidationObserver
    ref="typesFormObserver"
  >
    <div>
      <VTextField
        v-if="isTemplate"
        v-model="data.name"
        outlined
        :label="$t('analysisRequests.labels.template.name')"
        :rules="{ required: true }"
        @change="debouncedInput"
      />
      <VSelect
        v-model="data.client"
        :label="$t('forms.labels.client')"
        :items="displayedClients"
        cacheItems
        :loading="clientsLoading"
        :disabled="true"
        :rules="{ required: true }"
        :validateAtInitialization="isSiteDisabled"
        data-cy="selectClient"
        outlined
        @input="debouncedInput"
      />
      <VAutocomplete
        ref="autocompleteRef"
        v-model="data.site"
        :label="$t('forms.labels.site')"
        :items="displayedSites"
        :required="true"
        :disabled="isSiteDisabled"
        :validateAtInitialization="isSiteDisabled"
        :loading="sitesLoading"
        outlined
        data-cy="selectSite"
        @search="debouncedSiteSearch"
        @input="debouncedInput"
      >
        <template #withInfiniteScroll>
          <div v-intersect="endIntersect" />
        </template>
      </VAutocomplete>
      <SelectWithTreeview
        v-model="data.component"
        :label="$t('forms.labels.component')"
        :isOutlined="true"
        :items="availableTree"
        :loading="treeLoading"
        :required="true"
        :itemValue="availableTree.id ? 'id' : 'ref'"
        :validateAtInitialization="isWorkOrder || isEdition"
        :disabled="!data.site || isWorkOrder"
        data-cy="selectComponent"
        @search="debouncedSiteSearch"
        @input="onComponentChange"
      />
      <VSelect
        v-if="!isTemplate"
        v-model="data.template"
        :required="false"
        :label="$t('forms.labels.template')"
        itemText="name"
        itemValue="id"
        outlined
        :disabled="isEdition"
        :validateAtInitialization="isEdition"
        :items="getTemplates"
        data-cy="selectTemplate"
        @input="debouncedInput"
      />
      <VSelect
        v-model="data.type"
        :required="true"
        :label="$t('forms.labels.sampleType')"
        itemValue="key"
        outlined
        :disabled="templateLoading"
        :validateAtInitialization="isEdition || !!data.type"
        :items="sampleTypes"
        data-cy="selectType"
        @input="debouncedInputType"
      />
    </div>
  </ValidationObserver>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { debounce } from 'lodash';
import VAutocomplete from '@/components/shared/widgets/forms/VuetifyInputs/VAutocomplete.vue';
import VSelect from '@/components/shared/widgets/forms/VuetifyInputs/VSelect.vue';
import SelectWithTreeview from '@/components/shared/widgets/forms/SelectWithTreeview.vue';
import sampleTypes from '@/constants/sampleTypes';
import VTextField from '@/components/shared/widgets/forms/VuetifyInputs/VTextField.vue';
import treeMixin from '@/mixins/TreeMixin';
import { ValidationObserver } from 'vee-validate';

export default {
    components: {
        ValidationObserver,
        VAutocomplete,
        VSelect,
        SelectWithTreeview,
        VTextField,
    },
    mixins: [
        treeMixin,
    ],
    props: {
        mutableValue: {
            type: Object,
            default: null,
        },
        isWorkOrder: {
            type: Boolean,
            default: false,
        },
        isEdition: {
            type: Boolean,
            default: false,
        },
        isTemplate: {
            type: Boolean,
            default: false,
        },
        isSite: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            availableClients: this.mutableValue.clients || [],
            availableSites: this.mutableValue.sites || [],
            availableTree: this.mutableValue.tree || [],

            clientsLoading: false,
            sitesLoading: false,
            treeLoading: false,
            debouncedSiteSearch: debounce(this.searchSites, 500),

            data: {
                client: this.mutableValue.sample.client,
                site: this.mutableValue.sample.site,
                component: this.mutableValue.sample.component,
                type: this.mutableValue.sample.type,
                isTemplate: this.mutableValue.isTemplate,
                template: this.mutableValue.sample.template,
            },
            // search
            siteSearch: '',
            // infinite scroll
            sitePagination: {},
            debouncedInput: debounce(this.onInput, 500),
            debouncedInputType: debounce(this.onTypeChange, 500),
            templateLoading: false,
            availableTemplates: [],
            isValid: false,
        };
    },
    computed: {
        ...mapGetters([
            'getSample',
        ]),
        sampleTypes() {
            return Object.values(sampleTypes).map(type => ({
                name: this.$t(`sampleTypes.${type.charAt(0).toLowerCase()}${type.substring(1)}`),
                key: type,
            }));
        },
        displayedClients() {
            return this.availableClients.map(({ id, name, accountNumber }) => ({
                id,
                name: `${name} [${accountNumber}]`,
            }));
        },
        displayedSites() {
            return this.availableSites.map(({ id, name, deliveryAccountNumber }) => ({
                id,
                name: `${name} [${deliveryAccountNumber}]`,
            }));
        },

        dataSite() {
            this.$store.dispatch('setSiteInfo', this.data.site);
            return this.data.site;
        },
        isFormValid() {
            return !!(this.data.site && this.data.client && this.data.component && this.data.type);
        },
        getTemplates() {
            const templates = [{
                id: null,
                name: this.$t('labWorkOrderTable.labels.none'),
            }, ...this.availableTemplates?.map(tp => ({
                id: tp.id,
                name: tp.name,
            }))];
            return templates;
        },
        isSiteDisabled() {
            return this.isWorkOrder || this.isEdition || this.isTemplate || this.isSite;
        },
    },
    watch: {
        mutableValue: {
            handler() {
                if (this.mutableValue.clients) {
                    this.availableClients = this.mutableValue.clients;
                }
                if (this.mutableValue.sites) {
                    this.availableSites = this.mutableValue.sites;
                }
                if (this.mutableValue.tree) {
                    this.availableTree = this.mutableValue.tree;
                }
                if (this.mutableValue.sample) {
                    this.data = { ...this.mutableValue.sample };
                }
            },
            immediate: true,
        },
        async dataSite() {
            await this.setClient();
            await this.setComponent();
        },
        data: {
            handler() {
                if (this.data.component && !this.isTemplate) {
                    this.getARTemplateByComponent();
                }
                this.$emit('updateData', this.data);
            },
            deep: true,
        },
        'getSample.template': {
            handler() {
                if (this.getSample.template) {
                    const template = this.availableTemplates.find(tp => tp.id === this.getSample.template);
                    const infos = { ...template, template: template.id };
                    this.$emit('updateData', infos);
                    this.data = infos;
                }
            },
        },
        'getSample.type': {
            handler() {
                if (this.getSample.component) {
                    this.getTreatment();
                    this.$root.$emit('resetPreviousStep');
                }
            },
        },
    },
    async created() {
        await this.getSites();
        await this.getClients();
        // isWorkOrder prop use for creating AR from wo tree view
        if (this.isEdition || this.isWorkOrder || this.isTemplate || this.mutableValue?.site?.length) {
            await this.setClient();
            await this.setComponent();
            await this.getARTemplateByComponent();
        }
    },
    methods: {
        ...mapActions({
            setSiteInfo: 'setSiteInfo',

        }),
        // TODO search need to get fixed it never get called
        async searchSites(search) {
            this.siteSearch = search;
            await this.getSites();
        },
        async getClients() {
            // Si on est en mode edition, pas besoin de charger les 100 clients
            // Le champ client est grisé et n'affiche que le client du site selectionné
            if (this.isEdition) {
                this.clientsLoading = true;
                const { data } = await this.$rq.getClientInfo(this.mutableValue.sample.client);
                this.availableClients = [data];
                this.mutableValue.clients = data;
                this.clientsLoading = false;
                return;
            }


            this.clientsLoading = true;
            const { data } = !this.isWorkOrder ? await this.$rq.getClients({ allClients: true })
                : await this.$rq.getClientInfo(this.mutableValue.sample.client);
            const client = [data];
            this.availableClients = !this.isWorkOrder ? data.clients : client;
            this.mutableValue.clients = !this.isWorkOrder ? data.clients : client;

            this.clientsLoading = false;
        },
        async getSites(nextPage = 1) {
            // Si on est en mode edition, pas besoin de charger les 100 sites
            // Le champ site est grisé et n'affiche que le site selectionné
            if (this.isEdition) {
                this.sitesLoading = true;
                const { data } = await this.$rq.getSiteInfo(this.mutableValue.sample.site);
                const site = [data];
                this.availableSites = site;
                this.mutableValue.sites = site;
                this.sitesLoading = false;
                return;
            }

            this.sitesLoading = true;
            const queryParams = {
                ...this.sitePagination,
                page: nextPage,
            };
            if (this.siteSearch) {
                queryParams.search = this.siteSearch;
            }

            if (this.isSite) {
                queryParams.pagination = false;
            }
            const { data } = !this.isWorkOrder || this.isEdition ? await this.$rq.getSites(queryParams)
                : await this.$rq.getSiteInfo(this.mutableValue.sample.site);
            const site = this.isWorkOrder ? [data] : data.sites;
            if (data.pagination) {
                this.sitePagination = {
                    ...this.sitePagination,
                    ...data.pagination,
                };
            }
            this.availableSites = [...this.availableSites, ...site];
            if (this.mutableValue?.sites?.length) {
                this.mutableValue.sites = [...this.mutableValue.sites, ...site];
            } else {
                this.mutableValue.sites = data.sites;
            }
            this.sitesLoading = false;
        },
        async setClient() {
            this.clientsLoading = true;
            this.$emit('sites', this.availableSites);
            if (this.data.site) {
                const { data } = await this.$rq.getSiteInfo(this.data.site);
                this.data.client = data.client;
                this.$emit('clients', this.availableClients);
            }
            this.clientsLoading = false;
        },
        async setComponent() {
            this.treeLoading = true;
            if (this.data.site) {
                const { data } = await this.$rq.getNodeTree(this.data.site, 'site');
                this.addNodeWithEachName(data.tree);
                this.availableTree = data.tree;
                this.data.component = this.availableTree.length > 0 ? this.data.component : null;
                this.$emit('tree', this.availableTree);
            }
            this.treeLoading = false;
        },

        async onComponentChange(componentId) {
            this.$emit('inputChange', this.$refs.typesFormObserver.validate);
            const infos = {
                template: null,
                site: this.getSample.site,
                component: componentId,
                client: this.getSample.client,
                type: this.getSample.type,
            };
            this.data = { ...infos };

            this.$emit('updateData', {
                ...infos,
                ...this.mutableValue.empty,
                samples: [],
                name: null,
            });
        },

        async onInput() {
            this.$emit('inputChange', this.$refs.typesFormObserver.validate);
        },
        async onTypeChange() {
            this.$emit('inputChange', this.$refs.typesFormObserver.validate);
            // reset getSample values to non template values
            if (this.getSample.template?.length && !this.isTemplate) {
                const infos = {
                    template: null,
                    site: this.getSample.site,
                    component: this.getSample.component,
                    client: this.getSample.client,
                    type: this.getSample.type,
                };
                this.data = { ...infos };
                this.$emit('updateData', {
                    ...infos,
                    ...this.mutableValue.empty,
                    samples: [],
                    name: null,
                });
            }
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.sitePagination.currentPage < this.sitePagination.totalPageCount;
            if (isIntersecting && condition) {
                await this.getSites(this.sitePagination.currentPage + 1);
                // access "v-autocomplete" ref
                const ref = this.$refs?.autocompleteRef?.$refs?.autocomplete;
                if (ref?.onScroll) {
                    // workaround to refresh list after adding new items.
                    ref.onScroll();
                }
            }
        },
        async getARTemplateByComponent() {
            this.templateLoading = true;
            if (this.data.site && this.data.component) {
                const { data } = await this.$rq.getARTemplateByComponent(this.data.site, this.data.component);
                this.availableTemplates = data.templates;
            }
            this.templateLoading = false;
        },
        async getTreatment() {
            if (this.data.site && this.data.component && this.data.type) {
                const { data } = await this.$rq.getTreatmentName(this.data.site, this.data.component, {
                    type: this.data.type,
                });
                if (data?.length) {
                    this.$store.dispatch('setSample', { ...this.getSample, treatment: data });
                }
            }
        },
    },
};
</script>
